import { useEffect, useRef } from 'react';
import { hasOpenOverlay, isInToastRegion } from '../helpers/overlayHelper';
import { useNavigationStack } from '../context/NavigationStackContext';

/**
 * Escape does what the Back button does on a list or picker screen. It listens in the capture
 * phase, before an overlay's own Escape handling can close it, so an Escape that dismisses a
 * calendar, dropdown, popover or dialog is never also read as "go back".
 */
export const useEscapeToGoBack = (onBack?: () => void, isEnabled = true) => {
  const { goBack } = useNavigationStack();
  const latestBack = useRef<() => void>(() => goBack());
  latestBack.current = onBack ?? (() => goBack());
  const latestEnabled = useRef(isEnabled);
  latestEnabled.current = isEnabled;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!latestEnabled.current) return;
      if (event.key !== 'Escape' || event.defaultPrevented || event.isComposing) return;
      if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
      if (hasOpenOverlay() || isInToastRegion(event.target)) return;
      event.preventDefault();
      latestBack.current();
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, []);
};
